/* eslint-disable @typescript-eslint/no-explicit-any */
import { ChangeEvent, useMemo } from 'react';
import { Offcanvas } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { ColumnDef } from '@tanstack/react-table';
import FeatherIcon from 'feather-icons-react';
import { useState } from 'react';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';

import { PageBreadcrumbItem } from 'components/common/PageBreadcrumb';
import ArticlesTable from './ArticlesTable';
import SearchBox from 'components/common/SearchBox';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import PageBreadcrumb from 'components/common/PageBreadcrumb';
import useAdvanceTable from 'hooks/useAdvanceTable';
import Button from 'components/base/Button';
import CreateArticles from './CreateArticles';
import legalEntityArticleService from 'service/legalEntityArticle';
import { useWindowWidth } from 'utils/useWindowSize';

export type ArticlesType = {
  name: string;
  accounts_name: string;
  status: string;
  guid: string;
};

const Articles = () => {
  const { t, i18n } = useTranslation();
  const windowSiz: any = useWindowWidth();

  const articlesBreadcrumbItems: PageBreadcrumbItem[] = [
    {
      label: 'directories',
      url: '/'
    },
    {
      label: 'financial_account',
      active: true
    }
  ];
  const [openArticle, setOpenArticle] = useState(false);
  const [selectedArticleId, setSelectedArticleId] = useState('');

  //Get Articles
  const { data, refetch } = useQuery(['GET_ARTICLE'], () => {
    const res = legalEntityArticleService
      .getList({ offset: 0, limit: 100 })
      .then((res: any) => res?.legal_entity_articles);
    return res;
  });

  const articleData: ArticlesType[] = useMemo(() => {
    return (
      data?.map((items: any) => {
        return {
          name: items?.name?.[i18n?.language],
          accounts_name: items?.account?.name?.[i18n?.language],
          status: items?.status === true ? t('active') : t('blocked'),
          guid: items?.id
        };
      }) ?? []
    );
  }, [data, t, i18n?.language]);

  const articlesTableColumns: ColumnDef<ArticlesType>[] = [
    {
      id: 'action',
      cell: rowData => (
        <div className="d-flex justify-content-center gap-2">
          <Button
            variant="hover"
            className="p-2"
            onClick={() => {
              setOpenArticle(true);
              setSelectedArticleId(rowData?.row?.original?.guid);
            }}
          >
            <FeatherIcon icon="edit-2" className="cursor-pointer" size={20} />
          </Button>
        </div>
      ),
      meta: {
        headerProps: { style: { width: '7%' } },
        cellProps: { className: 'text-center' }
      }
    },
    {
      accessorKey: 'name',
      header: t('name_financial_account'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'accounts_name',
      header: t('account_name'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'status',
      header: t('status'),
      cell: rowData => {
        const { status } = rowData?.row?.original || {};
        /* prettier-ignore */
        const defaultColors: any = {
          [t('active')]: { bg: '#BBFBD0', text: '#119C2B' },
          [t('blocked')]: { bg: '#FDDFDD', text: '#F2271C' }
        };
        /* prettier-ignore */
        const backgroundColor =
         defaultColors[status]?.bg || '#ffffff'; // Default to white if not specified
        const textColor = defaultColors[status]?.text || '#000000';

        return (
          <div className="d-flex">
            <p
              style={{
                background: backgroundColor,
                padding: '8px 10px',
                textAlign: 'center',
                borderRadius: '8px',
                color: textColor,
                fontWeight: '600',
                margin: '0'
              }}
            >
              {status}
            </p>
          </div>
        );
      },
      meta: {
        cellProps: { className: 'text-900' }
      }
    }
  ];

  const table = useAdvanceTable({
    data: articleData,
    columns: articlesTableColumns,
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: false
  });

  const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    table.setGlobalFilter(e.target.value || undefined);
  };

  return (
    <div>
      <PageBreadcrumb items={articlesBreadcrumbItems} />
      <div className="mb-9">
        <h2 className="mb-5">{t('financial_account')}</h2>

        <AdvanceTableProvider {...table}>
          <div className="mb-4">
            <div className="d-flex justify-content-between gap-2">
              <SearchBox
                placeholder={t('search')}
                onChange={handleSearchInputChange}
              />

              <div className="d-flex justify-content-between gap-2">
                <Button
                  className="d-flex align-items-center gap-2"
                  variant="primary"
                  onClick={() => setOpenArticle(true)}
                >
                  <FontAwesomeIcon icon={faPlus} />
                  {windowSiz > 768 && t('add')}
                </Button>
              </div>
            </div>
          </div>
          <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 bg-white border-top border-bottom border-300 position-relative top-1">
            <ArticlesTable />
          </div>

          {/* <SupperModal
            show={openSupperModal}
            setShow={setOpenSupperModal}
            setConfirm={setConfirmToDelete}
            title={t('financial_account')}
            bodyText={t('you_want_delete')}
          /> */}

          <Offcanvas
            show={openArticle}
            onHide={() => {
              setOpenArticle(false);
              setSelectedArticleId('');
            }}
            placement="end"
            backdrop="static"
            keyboard={false}
          >
            <Offcanvas.Header closeButton>
              <Offcanvas.Title style={{ fontSize: '24px', fontWeight: '600' }}>
                {t('new')}
              </Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body>
              <CreateArticles
                setOpenArticle={setOpenArticle}
                selectedArticleId={selectedArticleId}
                refetch={refetch}
                setSelectedArticleId={setSelectedArticleId}
                articleData={articleData}
              />
            </Offcanvas.Body>
          </Offcanvas>
        </AdvanceTableProvider>
      </div>
    </div>
  );
};

export default Articles;
